import { state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, ElementRef, Injector, NgZone, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { AbstractComponent, Attendance, AttendanceCopilotActionsService, AttendanceCopilotInsightService, AttendanceCopilotStateService, AttendanceService, AttendanceStatusEnum, SalesMonitor, SalesMonitorStatus, SettingsModuleService, User } from 'lib-trend-core';
import { Observable, takeUntil } from 'rxjs';
import { AppState, PagerParamsState } from '../state/app.state';
import { attendanceSelector, copilotGateMessageCountSelector, openCloseSidebarRightSelector, simpleSalesMonitorsSelector } from '../state/selectors';

type SidebarTab = 'copilot' | 'contact' | 'opportunity';

@Component({
    selector: 'attendance-panel-info-component',
    // templateUrl: './attendance-panel-info.component.html',
    templateUrl: './attendance-panel-info.v2.component.html',
    styleUrls: ['./attendance-panel-info.component.scss'],
    animations: [
        trigger('slideInOut', [
            state('in', style({
                transform: 'translateX(0)',
                display: 'block',
            })),
            state('out', style({
                transform: 'translateX(100%)',
                display: 'none'
            })),
            transition('in => out', []),
            transition('out => in', [])
        ])
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AttendancePanelInfoComponent extends AbstractComponent implements OnInit {

  sidebarState: string = 'out';
  activeTab: SidebarTab = 'copilot';

  openCloseSidebarRight$: Observable<boolean> = this.store.select(openCloseSidebarRightSelector);
  readonly attendance$: Observable<Attendance> = this.store.select(attendanceSelector);
  readonly listSalesMonitors$: Observable<Array<SalesMonitor>> = this.store.select(simpleSalesMonitorsSelector);

  attendance: Attendance;
  copilotEnabled = false;
  /** The Copilot tab carries a marker while the conversation has an insight (RF-33). */
  insightAvailable = false;
  // Copilot "minimum messages" gate: the copilot actions/composer stay hidden
  // until the attendance accumulates copilotMinMessages real conversation messages.
  messagesTotal = 0;
  copilotMinMessages = 20;
  pagerParamsState: PagerParamsState;
  selectedUser: User;
  salesMonitor: SalesMonitorStatus;

  @ViewChild('transferModal') transferModal: TemplateRef<any>;
  dialogRef: MatDialogRef<any>;

  /** The height of the pinned tabs, handed to the Copilot tab as a CSS variable so its summary strip pins itself right under them. */
  tabsHeight = 0;
  private tabsObserver: ResizeObserver | null = null;

  // The tabs render conditionally, so the query is a setter: it follows the element as it comes and goes.
  @ViewChild('tabs') set tabs(el: ElementRef<HTMLElement> | undefined) {
    this.tabsObserver?.disconnect();
    this.tabsObserver = null;
    if (!el || typeof ResizeObserver === 'undefined') return;
    this.tabsObserver = new ResizeObserver(() => {
      const height = el.nativeElement.offsetHeight;
      this.zone.run(() => (this.tabsHeight = height));
    });
    this.tabsObserver.observe(el.nativeElement);
  }

  constructor(
    injector: Injector,
    private store: Store<AppState>,
    public dialog: MatDialog,
    private attendanceService: AttendanceService,
    private copilotStateService: AttendanceCopilotStateService,
    private copilotInsightService: AttendanceCopilotInsightService,
    private copilotActionsService: AttendanceCopilotActionsService,
    private settingsModuleService: SettingsModuleService,
    private readonly zone: NgZone,
  ) {
    super(injector);
  }

  /** Copilot unlocks once the attendance reaches the configured minimum messages. */
  get copilotReady(): boolean {
    return this.messagesTotal >= this.copilotMinMessages;
  }

  /** Closed attendances are read-only: copilot chat is shown, but no actions/composer. */
  get attendanceClosed(): boolean {
    return this.attendance?.status === AttendanceStatusEnum.CLOSED;
  }

  ngOnInit(): void {
    this.openCloseSidebarRight$.subscribe(isOpen => {
      this.sidebarState = isOpen ? 'out' : 'in';
    });

    this.store.select(copilotGateMessageCountSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe(total => this.messagesTotal = total ?? 0);

    this.copilotInsightService.select()
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => this.insightAvailable = !!state.view?.insight);

    this.attendance$
      .pipe(takeUntil(this.destroy$))
      .subscribe(attendance => {
        // The same attendance is emitted again after each update of it (first reply, transfer,
        // close, tag or origin edit, AI toggle): only opening another one resets the panel.
        const opened = !!attendance?._id && attendance._id !== this.attendance?._id;
        // A transfer changes who may mark the actions (RF-32).
        const reassigned = !opened && !!attendance?._id && this.assigneeOf(attendance) !== this.assigneeOf(this.attendance);
        this.attendance = attendance;
        if (reassigned) this.copilotActionsService.refresh();
        if (opened) {
          // The insight and the Actions (not the Copilot chat, see the note below) still follow the
          // previous attendance until this one's channel settings arrive: close them now, so its
          // content is never shown, marked or used in a reply over this one.
          this.copilotInsightService.close();
          this.copilotActionsService.close();
          this.loadCopilotEnabled(attendance);
          // NOTE: do NOT reset the copilot state here. This subscription fires on
          // every attendance update (e.g. a status change when closing), and an
          // unconditional reset would wipe the copilot chat without re-hydrating it.
          // The copilot tab already resets + re-hydrates when its idAttendance input
          // changes (and re-hydrates on close), which is the correct trigger.
        }
      });

  }

  private loadCopilotEnabled(attendance: Attendance): void {
    const channelId = (attendance.channel as any)?._id ?? attendance.channel;
    if (!this.getModuleCopilot() || !channelId) {
      this.applyCopilot(attendance, false);
      return;
    }
    this.settingsModuleService.getByChannel(channelId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        // A reply for an attendance no longer shown is dropped: applying it would open the
        // insight and the Actions of the previous attendance over the current one.
        next: (s: any) => {
          if (!this.isCurrent(attendance)) return;
          this.applyCopilot(attendance, s?.activeCopilotModule === true, s?.copilotMinMessages ?? 20);
        },
        error: () => {
          if (this.isCurrent(attendance)) this.applyCopilot(attendance, false);
        },
      });
  }

  private isCurrent(attendance: Attendance): boolean {
    return attendance._id === this.attendance?._id;
  }

  /** The insight and its actions follow the conversation only while its Copilot is active; they share a room, so they open and close together (RF-35, RF-39). */
  private applyCopilot(attendance: Attendance, enabled: boolean, minMessages = 20): void {
    this.copilotEnabled = enabled;
    this.copilotMinMessages = minMessages;
    this.resetTab();
    if (enabled) {
      this.copilotInsightService.open(attendance._id);
      this.copilotActionsService.open(attendance._id);
    } else {
      this.copilotInsightService.close();
      this.copilotActionsService.close();
    }
  }

  private assigneeOf(attendance: Attendance): string | null {
    const user = attendance?.user as any;
    return user?._id ?? user ?? null;
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.tabsObserver?.disconnect();
    this.copilotInsightService.close();
    this.copilotActionsService.close();
  }

  /** The tab an attendance opens on; the Opportunity tab does not depend on the Copilot and is kept while the CRM is on. */
  private resetTab(): void {
    if (this.activeTab === 'opportunity' && this.getModuleCRM()) return;
    this.activeTab = this.copilotEnabled ? 'copilot' : 'contact';
  }

  setActiveTab(tab: SidebarTab): void {
    this.activeTab = tab;
  }
}

